import { initVariationDropdowns } from './VariationDropdown'

const buyOverlayInstances = new WeakMap()

class BuyOverlay {
    constructor(buyOverlaySourceElement) {
        /* HTML Elements */
        this.buyOverlaySourceElement = buyOverlaySourceElement
        this.sourcePanelElements = this.buyOverlaySourceElement.querySelectorAll('.buy-overlay-panel')

        /* Rendered HTML Elements */
        this.backdropElement = null
        this.dialogElement = null
        this.closeButtonElement = null
        this.buyOverlayElement = null
        this.variationTextElement = null
        this.titleElement = null
        this.panelElements = []
        this.optionElements = []
        this.linkElements = []
        this.cartButtonElements = []

        /* Components */
        this.variationDropdowns = []

        /* Event Handlers */
        this.keyDownEventHandler = (event) => this.handleKeyDownEvent(event)
    }

    open(variationIndex) {
        this.close()
        document.body.insertAdjacentHTML('beforeend', this.getOverlayHTML())
        document.body.classList.add('buy-overlay-open')
        this.initOverlay(variationIndex)
    }

    getOverlayHTML() {
        return `
            <div class="buy-overlay-backdrop">
                <div class="buy-overlay-dialog">
                    <button
                        class="buy-overlay-close"
                        type="button"
                        aria-label="Schliessen"
                    ></button>
                    <div id="buy-overlay-grid">
                        ${this.buyOverlaySourceElement.innerHTML}
                    </div>
                </div>
            </div>
        `
    }

    getSourcePanelElement(variationIndex) {
        return Array.from(this.sourcePanelElements).find((panelElement) => {
            return Number(panelElement.dataset.variationIndex) === variationIndex
        })
    }

    initOverlay(variationIndex) {
        this.updateDomReferences()

        if (!this.buyOverlayElement) {
            return
        }

        this.initEventListeners()
        this.variationDropdowns = initVariationDropdowns(this.buyOverlayElement)
        this.updateSelectedVariation(variationIndex)
    }

    updateDomReferences() {
        this.backdropElement = document.querySelector('.buy-overlay-backdrop')

        if (!this.backdropElement) {
            return
        }

        this.dialogElement = this.backdropElement.querySelector('.buy-overlay-dialog')
        this.closeButtonElement = this.backdropElement.querySelector('.buy-overlay-close')
        this.buyOverlayElement = this.backdropElement.querySelector('.buy-overlay')
        this.variationDropdownElement = this.buyOverlayElement.querySelector('.variation-dropdown')
        this.variationTextElement = this.variationDropdownElement
            ? this.variationDropdownElement.querySelector('.variation-dropdown-text')
            : null
        this.titleElement = this.buyOverlayElement.querySelector('.buy-overlay-title')
        this.panelElements = this.buyOverlayElement.querySelectorAll('.buy-overlay-panel')
        this.optionElements = this.buyOverlayElement.querySelectorAll('.variation-dropdown-option')
        this.linkElements = this.buyOverlayElement.querySelectorAll('a')
        this.cartButtonElements = this.buyOverlayElement.querySelectorAll('.add-cart-button:not(.disabled)')
    }

    initEventListeners() {
        this.closeButtonElement.addEventListener('click', () => this.close())
        this.backdropElement.addEventListener('click', (event) => this.handleBackdropClickEvent(event))
        document.addEventListener('keydown', this.keyDownEventHandler)

        this.optionElements.forEach((optionElement) => {
            optionElement.addEventListener('click', () => {
                this.handleVariationOptionClickEvent(optionElement)
            })
        })

        this.linkElements.forEach((linkElement) => {
            linkElement.addEventListener('click', (event) => this.handleBuyLinkClickEvent(event))
        })

        this.cartButtonElements.forEach((cartButtonElement) => {
            cartButtonElement.addEventListener('click', () => setTimeout(() => this.close(), 0))
        })
    }

    handleVariationOptionClickEvent(optionElement) {
        this.updateSelectedVariation(Number(optionElement.dataset.variationIndex))
    }

    handleBackdropClickEvent(event) {
        if (event.target === this.backdropElement) {
            this.close()
        }
    }

    handleKeyDownEvent(event) {
        if (event.key === 'Escape') {
            this.close()
        }
    }

    updateSelectedVariation(variationIndex) {
        let selectedPanelElement = Array.from(this.panelElements).find((panelElement) => {
            return Number(panelElement.dataset.variationIndex) === variationIndex
        })
        let selectedOptionElement = Array.from(this.optionElements).find((optionElement) => {
            return Number(optionElement.dataset.variationIndex) === variationIndex
        })

        if (!selectedPanelElement) {
            return
        }

        if (this.variationTextElement) {
            this.variationTextElement.textContent = selectedOptionElement
                ? selectedOptionElement.textContent.trim()
                : selectedPanelElement.dataset.variationLabel
        }
        this.titleElement.textContent = selectedPanelElement.dataset.overlayLabel

        this.panelElements.forEach((panelElement) => {
            let isSelected = Number(panelElement.dataset.variationIndex) === variationIndex
            panelElement.classList.toggle('active', isSelected)
        })

        this.optionElements.forEach((optionElement) => {
            let isSelected = Number(optionElement.dataset.variationIndex) === variationIndex
            optionElement.hidden = isSelected
        })
    }

    handleBuyLinkClickEvent(event) {
        if (typeof ga !== 'undefined') {
            ga('send', 'event', {
                eventCategory: 'Kaufen Links',
                eventAction: 'click',
                eventLabel: event.target.getAttribute('data-eventlabel')
            })
        }
    }

    close() {
        document.removeEventListener('keydown', this.keyDownEventHandler)
        document.body.classList.remove('buy-overlay-open')
        this.variationDropdowns.forEach((variationDropdown) => variationDropdown.destroy())

        if (this.backdropElement) {
            this.backdropElement.remove()
        }

        this.backdropElement = null
        this.dialogElement = null
        this.closeButtonElement = null
        this.buyOverlayElement = null
        this.variationTextElement = null
        this.titleElement = null
        this.panelElements = []
        this.optionElements = []
        this.linkElements = []
        this.cartButtonElements = []
        this.variationDropdowns = []
    }
}

export function getBuyOverlay(titleDetailComponentElement) {
    let buyOverlaySourceElement = titleDetailComponentElement.querySelector('.buy-overlay-source')

    if (!buyOverlaySourceElement) {
        return null
    }

    if (!buyOverlayInstances.has(titleDetailComponentElement)) {
        buyOverlayInstances.set(titleDetailComponentElement, new BuyOverlay(buyOverlaySourceElement))
    }

    return buyOverlayInstances.get(titleDetailComponentElement)
}

export default function init() {
    const titleDetailComponentElements = document.querySelectorAll('.title-detail-v2-component')

    titleDetailComponentElements.forEach(titleDetailComponentElement => {
        getBuyOverlay(titleDetailComponentElement)
    })
}
